import { createSelector } from '@ngxs/store';
import { Entity, GetByIdFn, IdentifiedObject, StateModel } from './state.type';

export class EntityState {
  static hasError<T>(): (state: StateModel<T>) => boolean {
    const selectFn = (state: StateModel<T>): boolean => state.hasError;
    return createSelector([this], selectFn);
  }

  static isLoaded<T>(): (state: StateModel<T>) => boolean {
    const selectFn = (state: StateModel<T>): boolean => state.isLoaded;
    return createSelector([this], selectFn);
  }

  static getAll<T>(): (state: StateModel<T>) => T[] {
    const selectFn = (state: StateModel<T>): T[] =>
      EntityState.getEntitiesList(state);
    return createSelector([this], selectFn);
  }

  static getById<T>(): (state: StateModel<T>) => GetByIdFn<T | undefined> {
    const selectFn = (state: StateModel<T>): GetByIdFn<T | undefined> => {
      return (id: string) => state.entities[id];
    };
    return createSelector([this], selectFn);
  }

  static getSelected<T>(): (state: StateModel<T>) => T | undefined {
    const selectFn = (state: StateModel<T>): T | undefined =>
      state.entities[state.selected];
    return createSelector([this], selectFn);
  }

  static getSelectedId<T>(): (state: StateModel<T>) => string {
    const selectFn = (state: StateModel<T>): string => state.selected;
    return createSelector([this], selectFn);
  }

  static getEntitiesList<T>(state: StateModel<T>): T[] {
    return state.ids.map((id) => state.entities[id]);
  }

  static getUpdatedEntities<T extends IdentifiedObject>(
    state: StateModel<T>,
    newValues: T[],
  ): StateModel<T> {
    return {
      ...state,
      entities: {
        ...state.entities,
        ...EntityState.reduceListToEntities(newValues),
      },
    };
  }

  static getStateWithNewValues<T extends IdentifiedObject>(
    state: StateModel<T>,
    newValues: T[],
  ): StateModel<T> {
    const idsList = EntityState.mapEntitiesListToIdsList(newValues);
    const updatedEntities = EntityState.getUpdatedEntities(state, newValues);

    return {
      ...updatedEntities,
      ids: [...EntityState.addIdToListIfNotExists(state.ids, idsList)],
    };
  }

  static overwriteState<T extends IdentifiedObject>(
    state: StateModel<T>,
    newValues: T[],
  ): StateModel<T> {
    return {
      ...state,
      ...EntityState.getApiLoadSuccessConfig(),
      entities: EntityState.reduceListToEntities(newValues),
      ids: EntityState.mapEntitiesListToIdsList(newValues),
    };
  }

  static getFilteredState<T extends IdentifiedObject>(
    state: StateModel<T>,
    itemsToRemove: T[],
  ): StateModel<T> {
    return {
      ...state,
      entities: EntityState.filterEntities(state.entities, itemsToRemove),
      ids: EntityState.filterEntitiesIdsList(state.ids, itemsToRemove),
    };
  }

  static reduceListToEntities<T extends IdentifiedObject>(list: T[]): Entity<T> {
    const seed: Entity<T> = {};
    return list.reduce<Entity<T>>((prev, current) => {
      return { ...prev, [current.id]: current };
    }, seed);
  }

  static mapEntitiesListToIdsList<T extends IdentifiedObject>(
    entitiesList: T[],
  ): string[] {
    return entitiesList.map((object) => {
      if (typeof object.id === 'string') {
        return object.id;
      }
      return String(object.id);
    });
  }

  static filterEntities<T extends IdentifiedObject>(
    stateEntities: Entity<T>,
    itemsToRemove: T[],
  ): Entity<T> {
    return itemsToRemove.reduce<Entity<T>>((filteredEntity, currentItem) => {
      const { [currentItem.id]: _removedItem, ...remainingEntities } =
        filteredEntity;
      return remainingEntities;
    }, stateEntities);
  }

  static filterEntitiesIdsList<T extends IdentifiedObject>(
    entitiesIds: string[],
    itemsToRemove: T[],
  ): string[] {
    const shouldKeepId = (entityId: string): boolean => {
      return itemsToRemove.every((itemToRemove) => {
        return itemToRemove.id !== entityId;
      });
    };

    return entitiesIds.filter(shouldKeepId);
  }

  static addIdToListIfNotExists(
    currentList: string[],
    valuesToAdd?: string[],
  ): string[] {
    if (!valuesToAdd || !valuesToAdd.length) {
      return currentList;
    }

    const uniqueSet = new Set(currentList);
    valuesToAdd.forEach((value) => uniqueSet.add(value));
    return Array.from(uniqueSet);
  }

  static getApiLoadSuccessConfig(): { isLoaded: boolean; hasError: boolean } {
    return { isLoaded: true, hasError: false };
  }

  static getApiLoadErrorConfig(): { isLoaded: boolean; hasError: boolean } {
    return { isLoaded: true, hasError: true };
  }
}
